<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { useFleetStore } from '@/stores/fleetStore.ts'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'

/**
 * Props
 */
const props = defineProps({
  fleetIds: Array,
  smartPositioning: {
    type: Boolean,
    default: false
  }
})

/**
 * Stores / models
 */
const gameStore = useGameStore()
const fleetStore = useFleetStore()
const shipActionStore = useShipActionStore()
const mapStore = useMapStore()

/**
 * Variables
 */
const menuRef = ref(null)

/**
 * Functions
 */
function toggleMenu() {
  if (gameStore.pullDownMenuVisible === 'mapFleetMenu') {
    closeMenu()
  } else {
    gameStore.pullDownMenuVisible = 'mapFleetMenu'
  }
}

function closeMenu() {
  gameStore.pullDownMenuVisible = ''
}

const selectFleet = (fleetId) => {
  const fleet = fleetStore.findFleetById(fleetId)
  shipActionStore.initFleet(fleet)
  mapStore.disableAreaSelection()
  closeMenu()
}

function handleClickOutside(event) {
  if (!menuRef.value.contains(event.target)) {
    closeMenu()
  }
}

/**
 * Lifecycle hooks
 */
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Calculate dropdown position to keep it on screen
const dropdownPosition = computed(() => {
  if (!menuRef.value) return { top: '100%', left: '0' }

  // If smart positioning is disabled, always use default positioning
  if (!props.smartPositioning) {
    return { top: '100%', left: '0', transform: '' }
  }

  const menuWidth = 192 // w-48 = 192px
  const menuHeight = Math.max(160, props.fleetIds.length * 40) // Estimate height based on fleet count

  // Get map container dimensions
  const mapContainer = document.querySelector('.webgl-container')
  if (!mapContainer) return { top: '100%', left: '0' }

  const containerRect = mapContainer.getBoundingClientRect()
  const triggerRect = menuRef.value.parentElement?.getBoundingClientRect()

  if (!triggerRect) return { top: '100%', left: '0' }

  let top = '100%'
  let left = '0'
  let transform = ''

  // Check if dropdown would go off bottom of container
  if (triggerRect.bottom + menuHeight > containerRect.bottom) {
    // Flip to appear above, but still connected to the icon
    top = '0'
    transform = 'translateY(-100%)'
  }

  // Check if dropdown would go off right side of container
  if (triggerRect.left + menuWidth > containerRect.right) {
    // Align right edge of dropdown with right edge of trigger
    left = `-${menuWidth - triggerRect.width}px`
  }

  return { top, left, transform }
})
</script>

<template>
  <div class="relative" @click.stop="toggleMenu">
    <slot></slot>
    <!-- Dropdown Menu -->
    <Transition name="menu">
      <div
        v-show="gameStore.pullDownMenuVisible === 'mapFleetMenu'"
        ref="menuRef"
        class="absolute w-48 rounded-lg border border-metal-800 bg-metal-950 z-50 overflow-hidden"
        :style="{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          transform: dropdownPosition.transform
        }"
      >
        <template v-for="fleetId in fleetIds" :key="fleetId">
          <div class="menu-item" @click="selectFleet(fleetId)">
            {{ fleetStore.getFleetNameById(fleetId) }}
          </div>
        </template>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.menu-item {
  @apply flex items-center p-2 text-metal-300 text-sm cursor-pointer;
  transition: background-color 150ms ease;

  &:hover {
    @apply bg-metal-800;
  }

  &:active {
    @apply bg-metal-700;
  }
}

/* Menu transition */
.menu-enter-active,
.menu-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}

.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
